import React, { useCallback, useRef } from "react";
import { ReactTFC } from "../../../utils/fp/missing-ts-parts";

import { HeaderComponent } from "../../../components/HeaderComponent/HeaderComponent";
import { FooterComponent } from "../../../components/FooterComponent/FooterComponent";
import { HeaderSpacer } from "../../_layout-helpers/spacers";
import { ContentContainer } from "../../../components/_shared-components/arrangement/Container";

import { css } from "@emotion/react";

import { CSS_COLORS, CSS_SPACER_SIZES } from "../../../styles/styles-css-variables";
import { CSS_MQ } from "../../../styles/breakpoints";

import { PageMetadataComponent } from "../../../components/PageMetadataComponent/PageMetadataComponent";
import { ExtractSwarovskiPageData } from "@swo/storefront";
import { DealerLocatorPageData } from "@swo/storefront";
import { ModalLoadingSpinner } from "../../../routes/modal-loading-spinner";
import { DealerLocatorGoogleMapElement } from "../../../components/DealerLocatorComponent/subcomponents/DealerLocatorGoogleMapElement";
import { useInputElementFocusOnMount } from "../../../hooks/use-input-element-focus-on-mount";
import { FONTS } from "../../../styles/fonts";
import { useStore } from "../../../store/hooks/use-store";
import { getIsoCodeWithIntFallback } from "@swo/shared";
import { T } from "../../../components/T";

const Lazy_DealerLocatorComponent = React.lazy(() =>
  import("../../../components/DealerLocatorComponent/DealerLocatorComponent").then(m => ({
    default: m.DealerLocatorComponent,
  })),
);

export const DealerLocatorPageLayout: ReactTFC<ExtractSwarovskiPageData<DealerLocatorPageData>> = props => {
  const searchBarRef = useRef<HTMLInputElement | null>(null);
  const { locationOrigin, config } = useStore().state;
  const { googleApi } = config;

  const countryIsoCode = getIsoCodeWithIntFallback(props.locale.country);

  useInputElementFocusOnMount(searchBarRef);

  return (
    <React.Fragment>
      <PageMetadataComponent
        {...props.data.metadata}
        locationOrigin={locationOrigin}
        canonicalPath={props.page.path}
        languageIsoCode={props.locale.language.isoCode}
      />

      <HeaderComponent header={props.data.header} version={"normal"} />
      <HeaderSpacer />

      <ContentContainer>
        <PageHeadline>
          <T>{props.data.translations["dealer_locator_page.dealerLocatorLabel"]}</T>
        </PageHeadline>
      </ContentContainer>

      <React.Suspense
        fallback={
          <React.Fragment>
            <DealerLocatorGoogleMapElement />
            <ModalLoadingSpinner />
          </React.Fragment>
        }
      >
        <Lazy_DealerLocatorComponent
          ref={searchBarRef}
          countryIsoCode={props.locale.country === "INTERNATIONAL" ? "AT" : countryIsoCode}
          translations={props.data.translations}
          generalTranslations={props.generalTranslations}
          apiKey={googleApi.key}
        />
        <FooterComponent {...props.data.footer} />
      </React.Suspense>
    </React.Fragment>
  );
};

const PageHeadline: React.FC<React.PropsWithChildren> = props => {
  return (
    <h1
      css={css`
        ${FONTS.SwarovskiFutura300};

        color: ${CSS_COLORS.Green};
        text-align: center;
        text-transform: uppercase;

        font-size: 30px;
        letter-spacing: 1.25px;
        ${CSS_MQ.atLeastMd} {
          font-size: 48px;
          letter-spacing: 2px;
        }

        margin-top: ${CSS_SPACER_SIZES.md};
        margin-bottom: ${CSS_SPACER_SIZES.md};
      `}
    >
      {props.children}
    </h1>
  );
};
